import "./locationBloodDrawInformationAddComponent.scss";
import * as Yup from "yup";
import React, {useCallback, useEffect, useState} from "react";
import {Field, FieldArray, FieldProps, Form, Formik, FormikHelpers} from "formik";
import FormikInputComponent from "../../../../shared/components/core/formik-input/FormikInputComponent";
import FormikRadioButtonGroupComponent
    from "../../../../shared/components/core/formik-radio-button-group/formikRadioButtonGroupComponent";
import {CommonService} from "../../../../shared/services";
import FormikAutoCompleteDropdown
    from "../../../../shared/components/core/formik-auto-complete-dropdown/formikAutoCompleteDropdown";
import {APIConfig} from "../../../../constants";
import ButtonComponent from "../../../../shared/components/core/button/ButtonComponent";
import FormTitleWithNavigationBack
    from "../../../../shared/components/form-title-with-navigation-back/FormTitleWithNavigationBack";
import {Grid} from "@material-ui/core";
import FormikTimePickerComponent from "../../../../shared/components/core/formik-time-picker/formikTimePickerComponent";
import ChipComponent from "../../../../shared/components/chip/ChipComponent";
import {IAPIResponseType} from "../../../../shared/models/api.model";
import FeedbackComponent from "../../../../shared/components/feedback/feedbackComponent";
import FormikSelectDropdownComponent
    from "../../../../shared/components/core/formik-select-dropdown/FormikSelectDropdownComponent";
import FromikPhoneNumberComponent
    from "../../../../shared/components/core/fromik-phone-number/FromikPhoneNumberComponent";
import {useSearchParams} from "react-router-dom";

interface LocationBloodDrawInformationAddComponentProps {
    locationId: number;
    cancel: Function;
    prev: Function;
    locationDetails: any;
    next: Function;
}

const locationPickUpDaysAndTimesValidationSchema = Yup.object({
    day: Yup.mixed().required("Pickup Day is required"),
    time: Yup.string().required("Pickup Time is required")
        .nullable()
        .matches(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Please enter a valid time: hh:mm'),
})


const LocationBloodDrawInformationValidationSchema = Yup.object({
    isPhlebotomistAvailable: Yup.boolean().required('You must pick a Phlebotomist'),
    phlebotomistDetails: Yup.object().when('isPhlebotomistAvailable', {
        is: true,
        then: Yup.object({
            name: Yup.string()
                .required("Name  is required")
                .max(100, 'Name maximum limit can be 100 characters'),
            contactNumber: Yup.string().required("Contact Number  is required")
                .matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Mobile Number should be 10 digits"),
            draw: Yup.mixed().required("Draw is required"),
            address: Yup.string()
                .required("Address is required")
                .max(200, 'Address cannot exceed 200 characters'),
            contractNeeded: Yup.mixed().required("contract needed is required"),
            day: Yup.mixed(),
            time: Yup.string()
                .nullable()
                .matches(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Please enter a valid time: hh:mm'),
            pickUpDaysAndTimes: Yup.array(locationPickUpDaysAndTimesValidationSchema).min(1).required("Minimum one pickUp Day and Time is required"),
            zipcode: Yup.mixed().required("ZIP Code  is required"),
            state: Yup.mixed().required("State is required"),
            city: Yup.mixed().required("City is required"),
            newZipCode: Yup.string().when('zipcode', {
                is: (zipcode: any) => (zipcode?.zipcode && zipcode?.zipcode?.toLowerCase() === "other"),
                then: Yup.string().required('ZIP Code is required')
            }),
        })
    }),
})

interface LocationBloodDrawInformationFormSchema {
    isPhlebotomistAvailable: boolean;
    phlebotomistDetails: {
        name: string;
        contactNumber: string;
        draw: string,
        address: string,
        zipcode: any,
        newZipCode: string,
        city: string,
        state: string,
        contractNeeded: boolean,
        day: string,
        time: string,
        pickUpDaysAndTimes: any[],
    }
}

const formInitialValues: LocationBloodDrawInformationFormSchema = {
    isPhlebotomistAvailable: false,
    phlebotomistDetails: {
        name: "",
        contactNumber: "",
        draw: "",
        address: "",
        zipcode: {},
        newZipCode: "",
        city: "",
        state: "",
        day: "",
        time: "",
        contractNeeded: false,
        pickUpDaysAndTimes: []
    }
}

const LocationBloodDrawInformationAddComponent = (props: LocationBloodDrawInformationAddComponentProps) => {
    const {locationId, next, cancel, locationDetails, prev} = props;

    const [initialValues, setInitialValues] = useState<LocationBloodDrawInformationFormSchema>(formInitialValues);
    const [state, setState] = useState<string>("");
    const [isZipCodeNotFound, setIsZipCodeNotFound] = useState<boolean>(false);
    const [searchParams] = useSearchParams();
    const lastCompletedStep = searchParams.get("lastCompletedStep");

    const handleZipCodeValueChange = useCallback((zipCodeObject: any, setFieldValue: any) => {
        if (zipCodeObject === "") {
            setFieldValue("phlebotomistDetails.state", "");
            setFieldValue("phlebotomistDetails.city", "");
            setIsZipCodeNotFound(false);
            // setFieldValue("timezone", "");
            setState("");
        } else {
            if (zipCodeObject?.zipcode?.toLowerCase() === "other") {
                setIsZipCodeNotFound(true);
                setFieldValue("phlebotomistDetails.state", "");
                setFieldValue("phlebotomistDetails.city", "");
                // setFieldValue("timezone", "");
            } else {
                setIsZipCodeNotFound(false);
                setFieldValue("phlebotomistDetails.state", zipCodeObject.state);
                setFieldValue("phlebotomistDetails.city", zipCodeObject.city);
                // setFieldValue("timezone", zipCodeObject.timezone);
            }
        }
        setFieldValue("newZipCode", "");
    }, []);

    useEffect(() => {
        if (locationDetails?.bloodDrawInformation?.isPhlebotomistAvailable) {
            const locationInformationObj = {...locationDetails};
            const locationPhlebotomistDetailsObj = {...locationInformationObj.bloodDrawInformation?.phlebotomistDetails};
            const locationPhlebotomistDetails: any = {};
            locationPhlebotomistDetails['name'] = locationPhlebotomistDetailsObj?.name;
            locationPhlebotomistDetails['contactNumber'] = locationPhlebotomistDetailsObj?.contactNumber;
            locationPhlebotomistDetails['draw'] = locationPhlebotomistDetailsObj?.draw;
            locationPhlebotomistDetails['address'] = locationPhlebotomistDetailsObj?.address;
            if (!locationPhlebotomistDetailsObj?.zipcodeDetails) {
                locationPhlebotomistDetails['newZipCode'] = locationPhlebotomistDetailsObj?.addressDetails?.zipcode;
                locationPhlebotomistDetails['zipcode'] = {
                    zipcode: "Other"
                };
                locationPhlebotomistDetails['state'] = locationPhlebotomistDetailsObj?.addressDetails?.stateDetails?.state;
                locationPhlebotomistDetails['city'] = locationPhlebotomistDetailsObj?.addressDetails?.cityDetails?.city;
                setIsZipCodeNotFound(true);
            } else {
                locationPhlebotomistDetails['zipcode'] = locationPhlebotomistDetailsObj?.zipcodeDetails;
                locationPhlebotomistDetails['state'] = locationPhlebotomistDetailsObj?.zipcodeDetails?.state;
                locationPhlebotomistDetails['city'] = locationPhlebotomistDetailsObj?.zipcodeDetails?.city;
                setIsZipCodeNotFound(false);
            }
            locationPhlebotomistDetails['day'] = "";
            locationPhlebotomistDetails['time'] = "";
            locationPhlebotomistDetails['contractNeeded'] = locationPhlebotomistDetailsObj?.contractNeeded;
            locationPhlebotomistDetails['pickUpDaysAndTimes'] = locationPhlebotomistDetailsObj?.pickUpDaysAndTimes;
            setState(locationPhlebotomistDetailsObj?.addressDetails?.state);
            setInitialValues({
                isPhlebotomistAvailable: locationInformationObj?.bloodDrawInformation?.isPhlebotomistAvailable,
                phlebotomistDetails: locationPhlebotomistDetails
            });
        } else {
            setInitialValues(formInitialValues);
        }
    }, [locationDetails]);

    const onFormSubmit = useCallback((values: any, {
        setSubmitting,
        setErrors,
        validateForm
    }: FormikHelpers<any>) => {
        validateForm();
        values.phlebotomistDetails.contactNumber = values.phlebotomistDetails.contactNumber.replace(/[^0-9]/g, "");
        const phlebotomistDetails = values.phlebotomistDetails;
        delete phlebotomistDetails['day'];
        delete phlebotomistDetails['time'];
        if (phlebotomistDetails["newZipCode"]) {
            phlebotomistDetails["zipcode"] = phlebotomistDetails["newZipCode"];
            phlebotomistDetails["state"] = phlebotomistDetails?.state?.state;
            phlebotomistDetails["city"] = phlebotomistDetails?.city?.city;
            delete phlebotomistDetails["newZipCode"];
        } else {
            phlebotomistDetails["zipcode"] = phlebotomistDetails?.zipcode?.zipcode;
        }
        const payload = {
            "bloodDrawInformation": {
                "isPhlebotomistAvailable": values.isPhlebotomistAvailable,
                "phlebotomistDetails": phlebotomistDetails
            },
            lastCompletedStep:lastCompletedStep
        };
        setSubmitting(true);
        CommonService._location.addBloodFlowInformation(locationId, payload)
            .then((response: IAPIResponseType<any>) => {
                setSubmitting(false);
                next(response.data);
            })
            .catch((error: any) => {
                CommonService.handleErrors(setErrors, error);
                setSubmitting(false);
            })
    }, [locationId, next,lastCompletedStep]);

    return (
        <div className={'location-blood-draw-information-add-component screen t-form-screen'}>
            <FormTitleWithNavigationBack
                title={'Blood Draw Information'}
                tooltipData={"Include phlebotomist's information and pickup details, if present at the location."}
                handleClick={prev}
            />
            <div className="t-form t-form-back-navigation">
                <Formik
                    validationSchema={LocationBloodDrawInformationValidationSchema}
                    initialValues={initialValues}
                    enableReinitialize={true}
                    validateOnChange={false}
                    validateOnBlur={true}
                    validateOnMount={true}
                    onSubmit={onFormSubmit}
                >
                    {({
                          isSubmitting,
                          values,
                          isValid,
                          setFieldValue,
                          validateForm,
                      }) => {
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        useEffect(() => {
                            validateForm();
                        }, [validateForm, values]);
                        return (
                            <Form noValidate={true} className={"add-physician-form-holder"}>
                                <div className="t-form-controls-wrapper">
                                    <div className={'d-flex align-items-center mrg-bottom-20'}>
                                        <div className='physician-address-same-as-facility-header'>
                                            Do you have a phlebotomist?
                                        </div>
                                        <div className={'mrg-left-20 mrg-top-10'}>
                                            <Field name={'isPhlebotomistAvailable'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikRadioButtonGroupComponent
                                                            direction={"row"}
                                                            formikField={field}
                                                            titleKey={'title'}
                                                            valueKey={'code'}
                                                            isValueBoolean={true}
                                                            onUpdate={(value: any) => {
                                                            }}
                                                            options={CommonService._staticData.yesNoOptions}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    </div>
                                    {
                                        values.isPhlebotomistAvailable &&
                                        <>
                                            <Grid container spacing={2} xs={12}>
                                                <Grid item xs={12} sm={12} md={6} xl={6}>
                                                    <Field name={'phlebotomistDetails.name'}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikInputComponent
                                                                    label={'Phleb Name'}
                                                                    fullWidth={true}
                                                                    placeholder={'Enter Phleb Name*'}
                                                                    type={"text"}
                                                                    required={true}
                                                                    formikField={field}
                                                                />
                                                            )}
                                                    </Field>
                                                </Grid>
                                                <Grid item xs={12} sm={12} md={6} xl={6}>
                                                    <Field name={'phlebotomistDetails.contactNumber'}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FromikPhoneNumberComponent
                                                                    label={'Phleb Contact Number'}
                                                                    maxLength={14}
                                                                    placeholder={'Enter Phleb Contact Number'}
                                                                    required={true}
                                                                    formikField={field}
                                                                />
                                                            )}
                                                    </Field>
                                                </Grid>
                                            </Grid>
                                            <Grid container spacing={2} xs={12}>
                                                <Grid item xs={12} sm={12} md={6} xl={6}>
                                                    <Field name={'phlebotomistDetails.draw'}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikAutoCompleteDropdown
                                                                    label={'Draw'}
                                                                    placeholder={'Select Draw'}
                                                                    required={true}
                                                                    valueKey={'code'}
                                                                    data={CommonService._staticData.labType}
                                                                    formikField={field}
                                                                    getOptionLabel={(option => (option.title) || '')}

                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </Grid>
                                                <Grid item xs={12} sm={12} md={6} xl={6}>
                                                    <Field name={'phlebotomistDetails.contractNeeded'}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikAutoCompleteDropdown
                                                                    label={'Phleb Contract Needed'}
                                                                    placeholder={'Select Phleb Contract Needed'}
                                                                    required={true}
                                                                    valueKey={'code'}
                                                                    data={CommonService._staticData.yesNoOptions}
                                                                    formikField={field}
                                                                    getOptionLabel={(option => (option.title) || '')}

                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </Grid>
                                            </Grid>
                                            <Grid container spacing={2} xs={12}>
                                                <Grid item xs={12} sm={12} md={6} xl={6}>
                                                    <Field name={'phlebotomistDetails.address'}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikInputComponent
                                                                    label={'Phleb Address'}
                                                                    fullWidth={true}
                                                                    placeholder={'Enter Phleb Address'}
                                                                    type={"text"}
                                                                    required={true}
                                                                    formikField={field}
                                                                />
                                                            )}
                                                    </Field>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Field name={'phlebotomistDetails.zipcode'}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikAutoCompleteDropdown
                                                                    label={'Phleb ZIP Code'}
                                                                    placeholder={'Start typing Phleb ZIP Code'}
                                                                    required={true}
                                                                    formikField={field}
                                                                    fullWidth={true}
                                                                    openOnFocus={true}
                                                                    url={APIConfig.ZIPCODE_LIST.URL}
                                                                    method={APIConfig.ZIPCODE_LIST.METHOD}
                                                                    searchMode={'serverSide'}
                                                                    data={[]}
                                                                    valueKey={'zipcode'}
                                                                    dataListKey={"data.docs"}
                                                                    onUpdate={(value: any) => {
                                                                        handleZipCodeValueChange(value, setFieldValue)
                                                                    }}
                                                                    //Hiding other option from zipcode
                                                                    // defaultData={[
                                                                    //     {
                                                                    //         zipcode: "Other"
                                                                    //     }
                                                                    // ]}
                                                                    getOptionLabel={(option => (option.zipcode) || '')}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </Grid>
                                            </Grid>
                                            {isZipCodeNotFound && <>
                                                <Grid container spacing={2} xs={12}>
                                                    <Grid item xs={12} sm={6}>
                                                        <Field name={'phlebotomistDetails.newZipCode'}>
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikInputComponent
                                                                        placeholder={"Enter New ZIP Code"}
                                                                        label={'New ZIP Code'}
                                                                        required={true}
                                                                        maxLength={5}
                                                                        type={"text"}
                                                                        formikField={field}
                                                                    />
                                                                )
                                                            }
                                                        </Field>

                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <Field name={'phlebotomistDetails.state'}>
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikAutoCompleteDropdown
                                                                        label={'Phleb State'}
                                                                        required={true}
                                                                        placeholder={"start typing Phleb State*"}
                                                                        formikField={field}
                                                                        url={APIConfig.STATE_LIST.URL}
                                                                        method={APIConfig.STATE_LIST.METHOD}
                                                                        searchMode={'serverSide'}
                                                                        openOnFocus={true}
                                                                        dataListKey={"data.docs"}
                                                                        data={[]}
                                                                        valueKey={'state'}
                                                                        onUpdate={(value: any) => {
                                                                            setState(value?.state);
                                                                            field.form.setFieldValue("city", '');
                                                                        }}
                                                                        getOptionLabel={(option => (option.state) || '')}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </Grid>
                                                </Grid>
                                                <Grid container spacing={2} xs={12}>
                                                    <Grid item xs={12} sm={6}>
                                                        <Field name={'phlebotomistDetails.city'}>
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikAutoCompleteDropdown
                                                                        label={'Phleb City'}
                                                                        placeholder={'Phleb City'}
                                                                        required={true}
                                                                        id="city_auto_complete"
                                                                        formikField={field}
                                                                        searchMode={'serverSide'}
                                                                        url={APIConfig.CITY_LIST.URL}
                                                                        method={APIConfig.CITY_LIST.METHOD}
                                                                        disabled={!state}
                                                                        payload={{
                                                                            state: state,
                                                                            page: 1,
                                                                            limit: 100
                                                                        }}
                                                                        valueKey={'city'}
                                                                        data={[]}
                                                                        getOptionLabel={(option => (option.city) || '')}
                                                                        dataListKey={"data.docs"}
                                                                    />
                                                                )
                                                            }
                                                        </Field>

                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>

                                                    </Grid>
                                                </Grid>
                                            </>
                                            }
                                            {
                                                !isZipCodeNotFound && values?.phlebotomistDetails?.zipcode?.zipcode && values?.phlebotomistDetails?.zipcode?.zipcode !== "Other" &&
                                                <Grid container spacing={2} xs={12}>
                                                    <Grid item xs={12} sm={6}>
                                                        <Field name={'phlebotomistDetails.state'}>
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikInputComponent
                                                                        label={'Phleb State'}
                                                                        placeholder={'Enter Phleb State'}
                                                                        required={true}
                                                                        readOnly={true}
                                                                        formikField={field}
                                                                        type={"text"}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <Field name={'phlebotomistDetails.city'}>
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikInputComponent
                                                                        label={'Phleb City'}
                                                                        placeholder={'Enter Phleb City'}
                                                                        required={true}
                                                                        readOnly={true}
                                                                        formikField={field}
                                                                        type={"text"}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </Grid>
                                                </Grid>
                                            }
                                            <div className={"horizontal-line"}></div>
                                            <div className={"form-sub-title"}>Pickup Days and Time</div>
                                            <FieldArray
                                                name={"phlebotomistDetails.pickUpDaysAndTimes"}
                                                render={(arrayHelpers) => (
                                                    <>
                                                        <Grid container spacing={2} xs={12}>
                                                            <Grid item xs={12} sm={12} md={6} xl={6}>
                                                                <Field name={'phlebotomistDetails.day'}>
                                                                    {
                                                                        (field: FieldProps) => (
                                                                            <FormikSelectDropdownComponent
                                                                                label={'Pickup Day'}
                                                                                placeholder={'Select Pickup Day'}
                                                                                required={true}
                                                                                options={CommonService._staticData.weekDays}
                                                                                formikField={field}
                                                                                valueKey={'code'}
                                                                                getOptionLabel={(option => (option.title) || '')}
                                                                            />
                                                                        )
                                                                    }
                                                                </Field>
                                                            </Grid>
                                                            <Grid item xs={12} sm={12} md={6} xl={6}>
                                                                <Field name={'phlebotomistDetails.time'}>
                                                                    {
                                                                        (field: FieldProps) => (
                                                                            <FormikTimePickerComponent
                                                                                label={'Pickup Time'}
                                                                                placeholder={'Select Pickup Time'}
                                                                                required={true}
                                                                                fullWidth={true}
                                                                                formikField={field}
                                                                            />
                                                                        )
                                                                    }
                                                                </Field>
                                                            </Grid>
                                                        </Grid>
                                                        <div className={"d-flex justify-content-flex-end"}>
                                                            <ButtonComponent
                                                                type="button"
                                                                color={"secondary"}
                                                                variant={"outlined"}
                                                                fullWidth={false}
                                                                disabled={(values.phlebotomistDetails.day === "" || (values.phlebotomistDetails.time === "" || values.phlebotomistDetails.time === null)) || isSubmitting}
                                                                handleClick={() => {
                                                                    arrayHelpers.push({
                                                                        "day": values.phlebotomistDetails.day,
                                                                        "time": values.phlebotomistDetails.time
                                                                    })
                                                                    setFieldValue("phlebotomistDetails.day", "");
                                                                    setFieldValue("phlebotomistDetails.time", "");
                                                                }}
                                                            >
                                                                Add
                                                            </ButtonComponent>
                                                        </div>
                                                        {
                                                            values?.phlebotomistDetails.pickUpDaysAndTimes.length === 0 &&
                                                            <FeedbackComponent className={'width-50 margin-top20'}>
                                                                No Pickup Day and Time have been added.
                                                            </FeedbackComponent>
                                                        }
                                                        <div className={'display-flex-with-column-gap mrg-top-20'}>
                                                            {values?.phlebotomistDetails.pickUpDaysAndTimes && values.phlebotomistDetails.pickUpDaysAndTimes.map((dayAndTime: any, index: any) => {
                                                                    return (
                                                                        <div key={index}>
                                                                            <ChipComponent
                                                                                label={`${CommonService.capitalizeFirstLetter(dayAndTime.day)} | ${dayAndTime.time}`}
                                                                                onDelete={arrayHelpers.handleRemove(index)}
                                                                            />
                                                                        </div>
                                                                    )
                                                                }
                                                            )
                                                            }
                                                        </div>

                                                    </>
                                                )
                                                }
                                            />
                                        </>
                                    }
                                </div>
                                <div className="form-action-section">
                                    <ButtonComponent
                                        type="button"
                                        color={"primary"}
                                        variant={"outlined"}
                                        fullWidth={true}
                                        disabled={isSubmitting}
                                        handleClick={cancel}
                                    >
                                        Close
                                    </ButtonComponent>
                                    <ButtonComponent
                                        type="submit"
                                        color={"primary"}
                                        variant={"contained"}
                                        fullWidth={true}
                                        disabled={!isValid}
                                        isLoading={isSubmitting}
                                    >
                                        Submit
                                    </ButtonComponent>
                                </div>
                            </Form>
                        )
                    }
                    }
                </Formik>
            </div>
        </div>
    );

};

export default LocationBloodDrawInformationAddComponent;
